import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LogOut, BarChart3, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4"; 

export default function Navbar() {
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  const isDemoUser = user?.email === "Demo@insightsflows.com";
  const [expandBanner, setExpandBanner] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const bannerRef = useRef(null);

    const handleWhatsClick = () => {
    ReactGA.event({
      category: "Button",
      action: "Click",
      label: "WhatsApp Clicked",
    });
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (bannerRef.current) {
        const { scrollWidth, clientWidth } = bannerRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [t, expandBanner]);

  return (
    <>
      {/* HEADER */}
      <header className="sticky top-0 bg-white shadow-xl z-30">
        <div className="flex justify-between items-center px-4 py-4">
          {/* Logo + Title */}
          <div className="flex items-center">
            <BarChart3 className="text-blue-500 mr-2" size={28} />
            <Link to="/reports/dept/Report">
              <h1 className="text-2xl font-bold text-gray-800 tracking-wide hover:text-blue-600 transition whitespace-nowrap">
                {t("navbarTitle")}
              </h1>
            </Link>
          </div>

          {/* Logout */}
          <motion.button
            onClick={logout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition duration-200 ease-in-out flex items-center whitespace-nowrap"
          >
            <LogOut size={18} className="mr-2" />
            <span className="hidden sm:inline">{t("logout")}</span>
          </motion.button>
        </div>

        {/* DEMO BANNER */}
        {isDemoUser && (
          <div className="w-full bg-yellow-100 border-t border-b border-yellow-300 text-yellow-800 text-sm sm:text-base px-4 py-2">
            <div className="flex justify-between items-center w-full gap-4 overflow-x-hidden whitespace-nowrap">
              {/* Left Section: Icon + Text + Toggle */}
              <div className="flex items-center gap-2 overflow-hidden min-w-0">
                <AlertTriangle className="text-yellow-500 flex-shrink-0" size={20} />
                {!expandBanner ? (
                  <div ref={bannerRef} className="truncate max-w-[60vw] sm:max-w-full">
                    {t("demoBanner")}
                  </div>
                ) : (
                  <div className="whitespace-normal">{t("demoBanner")}</div>
                )}
                {/* Toggle Icon */}
                {isOverflowing && !expandBanner && (
                  <button
                    onClick={() => setExpandBanner(true)}
                    className="text-blue-700 hover:text-blue-900"
                  >
                    <ChevronDown size={16} />
                  </button>
                )}
                {expandBanner && (
                  <button
                    onClick={() => setExpandBanner(false)}
                    className="text-blue-700 hover:text-blue-900"
                  >
                    <ChevronUp size={16} />
                  </button>
                )}
              </div>

              {/* Right Side Buttons */}
              <div className="flex gap-4 text-blue-700 font-medium flex-nowrap">
                <Link to="/ContactUsPage" className="hover:underline hover:text-blue-900 whitespace-nowrap">
                  {t("contactUs")}
                </Link>
                <a
                  href="https://wa.me/966582076578?text=Hello%2C%20I%20need%20assistance%20with%20InsightsFlows."
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWhatsClick}

                  className="hover:underline hover:text-blue-900 whitespace-nowrap"
                >
                  {t("chatWithUs")}
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
